/*
 * U&I Virtual Keyboard
 * Version 10
 * Shopify Horizon + Easify
 * Full conditional-logic build
 */

(function () {
  "use strict";

  const TEXT_FIELD_SELECTOR =
    'input[type="text"], input:not([type]), textarea';

  const nativeInputValueSetter =
    Object.getOwnPropertyDescriptor(
      HTMLInputElement.prototype,
      "value"
    )?.set;

  const nativeTextAreaValueSetter =
    Object.getOwnPropertyDescriptor(
      HTMLTextAreaElement.prototype,
      "value"
    )?.set;

  function setNativeFieldValue(field, value, notify = true) {
    const setter =
      field instanceof HTMLTextAreaElement
        ? nativeTextAreaValueSetter
        : nativeInputValueSetter;

    if (setter) {
      setter.call(field, value);
    } else {
      field.value = value;
    }

    if (!notify) return;

    field.dispatchEvent(
      new Event("input", { bubbles: true })
    );

    field.dispatchEvent(
      new Event("change", { bubbles: true })
    );
  }

  function openKeyboardModalForInput(input) {
    if (!input) return;

    document.getElementById("vk-modal")?.remove();

    const modal = document.createElement("div");
    modal.id = "vk-modal";

    modal.innerHTML = `
      <div class="vk-backdrop"></div>

      <div
        class="vk-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Hebrew keyboard"
      >
        <div class="vk-header">
          <button
            class="vk-action vk-close"
            type="button"
            aria-label="Close"
          >
            ✕
          </button>

          <button
            class="vk-action vk-done"
            type="button"
            aria-label="Apply and Close"
          >
            Apply &amp; Close
          </button>
        </div>

        <div class="vk-body">
          <div class="vk-output">
            <input
              type="text"
              class="vk-output-text"
              dir="rtl"
              placeholder="Tap to start"
            >
          </div>

          <div class="vk-keyboard"></div>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    const output = modal.querySelector(".vk-output-text");
    const closeBtn = modal.querySelector(".vk-close");
    const doneBtn = modal.querySelector(".vk-done");
    const backdrop = modal.querySelector(".vk-backdrop");

    output.value = input.value || "";

    doneBtn.style.display = "none";
    closeBtn.style.display = "inline-flex";

    function closeModal() {
      modal.remove();
    }

    closeBtn.addEventListener("click", closeModal);
    doneBtn.addEventListener("click", closeModal);
    backdrop.addEventListener("click", closeModal);

    if (
      !window.SimpleKeyboard ||
      !window.SimpleKeyboardLayouts
    ) {
      console.error(
        "[U&I Virtual Keyboard] " +
        "Simple Keyboard libraries did not load."
      );

      modal.remove();
      return;
    }

    const Keyboard = window.SimpleKeyboard.default;
    const KeyboardLayouts = window.SimpleKeyboardLayouts.default;
    const baseLayout = new KeyboardLayouts().get("hebrew");

    if (!baseLayout || !baseLayout.layout) {
      console.error(
        "[U&I Virtual Keyboard] " +
        "Hebrew layout was not found."
      );

      modal.remove();
      return;
    }

    const layout =
      typeof structuredClone === "function"
        ? structuredClone(baseLayout)
        : JSON.parse(JSON.stringify(baseLayout));

    layout.layout.default = layout.layout.default
      .map(row =>
        row
          .replaceAll("{tab}", "")
          .replaceAll("{lock}", "")
          .replaceAll("{shift}", "")
          .replaceAll("{enter}", "")
          .replaceAll(".com", "")
          .replaceAll("@", "")
          .trim()
      )
      .filter(Boolean);

    /* Keep the Version 9 Hebrew gershayim key behavior. */
    const spaceRowIndex = layout.layout.default.findIndex(
      row => row.includes("{space}")
    );

    const bottomLetterRowIndex =
      spaceRowIndex > 0
        ? spaceRowIndex - 1
        : layout.layout.default.length - 1;

    const bottomLetterRowKeys =
      layout.layout.default[bottomLetterRowIndex].split(/\s+/);

    if (!bottomLetterRowKeys.includes("״")) {
      bottomLetterRowKeys.push("״");
      layout.layout.default[bottomLetterRowIndex] =
        bottomLetterRowKeys.join(" ");
    }

    function syncValue(value) {
      const nextValue = String(value ?? "");

      output.value = nextValue;

      setNativeFieldValue(input, nextValue, true);

      const hasText = Boolean(nextValue.trim().length);

      closeBtn.style.display = hasText ? "none" : "inline-flex";
      doneBtn.style.display = hasText ? "inline-flex" : "none";
    }

    const keyboard = new Keyboard(
      modal.querySelector(".vk-keyboard"),
      {
        ...layout,

        display: {
          ...(layout.display || {}),
          "{bksp}": "Delete",
          "{space}": "Space"
        },

        onChange: value => syncValue(value)
      }
    );

    keyboard.setInput(input.value || "");
    syncValue(input.value || "");

    output.addEventListener("input", () => {
      keyboard.setInput(output.value);
      syncValue(output.value);
    });

    modal.classList.add("active");
    output.focus();
  }

  function isVisible(element) {
    if (!element || element.hidden) return false;

    if (element.getAttribute("aria-hidden") === "true") {
      return false;
    }

    return element.getClientRects().length > 0;
  }

  function isSupportedTextField(field) {
    if (field instanceof HTMLTextAreaElement) return true;

    return (
      field instanceof HTMLInputElement &&
      field.type === "text"
    );
  }

  function isProductOptionField(field) {
    const fieldName = field.getAttribute("name") || "";

    return Boolean(
      field.closest('form[action*="/cart/add"]') ||
      fieldName.startsWith("properties[") ||
      field.closest(
        '[class*="easify" i], ' +
        '[id*="easify" i], ' +
        '[data-easify]'
      )
    );
  }

  function shouldSkipField(field) {
    if (!field || !isSupportedTextField(field)) return true;
    if (field.closest("#vk-modal")) return true;
    if (!isProductOptionField(field)) return true;
    if (!isVisible(field)) return true;
    if (field.readOnly || field.disabled) return true;
    if (field.closest("select")) return true;
    if (field.closest(".select")) return true;
    if (field.getAttribute("role") === "combobox") return true;

    if (field.getAttribute("aria-haspopup") === "listbox") {
      return true;
    }

    return false;
  }

  function enhanceField(field) {
    if (!field || !isSupportedTextField(field)) return;
    if (field.closest("#vk-modal")) return;
    if (!isProductOptionField(field)) return;

    if (field.dataset.vkApplied === "true") return;
    if (shouldSkipField(field)) return;

    const wrapper = field.parentElement;
    if (!wrapper) return;

    const existingTrigger = wrapper.querySelector(
      ":scope > .vk-trigger"
    );

    if (existingTrigger) {
      field.dataset.vkApplied = "true";
      return;
    }

    /* Preserve the Easify field's existing dimensions and styling. */
    if (getComputedStyle(wrapper).position === "static") {
      wrapper.style.position = "relative";
    }

    const trigger = document.createElement("span");

    trigger.textContent = "Hebrew";
    trigger.className = "vk-trigger";
    trigger.setAttribute("role", "button");
    trigger.setAttribute("tabindex", "0");

    Object.assign(trigger.style, {
      position: "absolute",
      top: "50%",
      right: "12px",
      transform: "translateY(-50%)",
      zIndex: "2",
      textDecoration: "underline",
      cursor: "pointer",
      userSelect: "none",
      whiteSpace: "nowrap"
    });

    trigger.addEventListener("mousedown", event => {
      event.preventDefault();
    });

    trigger.addEventListener("click", () => {
      openKeyboardModalForInput(field);
    });

    trigger.addEventListener("keydown", event => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        openKeyboardModalForInput(field);
      }
    });

    wrapper.appendChild(trigger);
    field.dataset.vkApplied = "true";
  }

  function scanForTextFields(root) {
    if (!root) return;

    if (
      root instanceof Element &&
      root.matches(TEXT_FIELD_SELECTOR)
    ) {
      enhanceField(root);
    }

    root
      .querySelectorAll?.(TEXT_FIELD_SELECTOR)
      .forEach(enhanceField);
  }

  function startVirtualKeyboard() {
    if (!document.body) return;

    if (document.body.dataset.vkObserverStarted === "true") {
      return;
    }

    document.body.dataset.vkObserverStarted = "true";
    scanForTextFields(document.body);

    const observer = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        if (mutation.type === "childList") {
          for (const node of mutation.addedNodes) {
            if (node instanceof HTMLElement) {
              scanForTextFields(node);
            }
          }
        }

        if (
          mutation.type === "attributes" &&
          mutation.target instanceof HTMLElement
        ) {
          scanForTextFields(mutation.target);
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [
        "class",
        "style",
        "hidden",
        "aria-hidden"
      ]
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      startVirtualKeyboard,
      { once: true }
    );
  } else {
    startVirtualKeyboard();
  }

  document.addEventListener("shopify:section:load", event => {
    scanForTextFields(event.target);
  });
})();